/**
 * Plain words for an audit row. The API names an action after its code
 * (`organization.updateOrganization`): the handler's leading verb gives the
 * label, the HTTP method when it starts with none of ours.
 */
export const VERBS = [
  'activate',
  'add',
  'analyze',
  'apply',
  'archive',
  'book',
  'cancel',
  'confirm',
  'create',
  'decide',
  'delete',
  'discard',
  'duplicate',
  'email',
  'forgot',
  'impersonate',
  'login',
  'pause',
  'propose',
  'purge',
  'remove',
  'resend',
  'reset',
  'resume',
  'resync',
  'retry',
  'rotate',
  'save',
  'send',
  'submit',
  'sync',
  'toggle',
  'unarchive',
  'update',
  'validate',
];

const METHOD_VERBS = {
  POST: 'create',
  PUT: 'update',
  PATCH: 'update',
  DELETE: 'delete',
};

export function actionVerb({ action, httpMethod }) {
  const handler = action?.split('.').pop() ?? '';
  const first = handler.match(/^[a-z]+/)?.[0];
  return VERBS.includes(first) ? first : (METHOD_VERBS[httpMethod] ?? 'update');
}

/** organization-api-keys -> Organization api key, for types without a label. */
export function humanizeType(type) {
  const words = type.replace(/-/g, ' ').replace(/ies$/, 'y').replace(/s$/, '');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const actionLabel = (t, row) => t(`verbs.${actionVerb(row)}`);

export const resourceTypeLabel = (t, type) =>
  t(`resourceTypes.${type}`, { defaultValue: humanizeType(type) });

/** "Easia Travel" when the API found a name, else "#12". */
export const resourceName = (row) =>
  row.resourceName ?? (row.resourceId ? `#${row.resourceId}` : '');

const VERB_TONES = {
  add: 'create',
  create: 'create',
  duplicate: 'create',
  book: 'create',
  cancel: 'delete',
  delete: 'delete',
  discard: 'delete',
  purge: 'delete',
  remove: 'delete',
  forgot: 'access',
  impersonate: 'access',
  login: 'access',
  email: 'send',
  resend: 'send',
  send: 'send',
  archive: 'archive',
  unarchive: 'unarchive',
};

/**
 * create, delete, access, send, archive, unarchive or edit: picks the
 * timeline dot's icon and color.
 */
export const verbTone = (row) => VERB_TONES[actionVerb(row)] ?? 'edit';

/** Local calendar day of a date, as YYYY-MM-DD. */
export const dayKey = (value) => {
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Rows (newest first) as [{ day, rows }], one group per local day, in order. */
export function groupByDay(rows) {
  const groups = [];
  rows.forEach((row) => {
    const day = dayKey(row.occurredAt);
    const last = groups[groups.length - 1];
    if (last?.day === day) last.rows.push(row);
    else groups.push({ day, rows: [row] });
  });
  return groups;
}
